<template>
  <v-card class="elevation-0">

    <v-select
      v-model="selectedSerialNumbers"
      :items="kioskNames"
      :multiple="multiple"
      :disabled="disabled"
      :color="selectColor"
      readonly
      chips
      class="mb-3 mr-2"
      hide-details="auto"
      ref="input"
      >
      <template v-slot:label>
        <span :class="selectLabelClass">{{ label }}</span>
      </template>
      <template v-slot:append>
        <v-icon
          large
          @click="pickKiosks()"
          >add_to_queue
        </v-icon>
      </template>

      <template v-slot:selection="{ item }">
        <v-chip
          dark
          close
          close-icon="mdi-close-outline"
          :color="chipColor"
          @click:close="removeKiosk(item)"
          >
          <span class="text-truncate">{{ item }}</span>
        </v-chip>
      </template>
    </v-select>
    <p v-if="error" class="red--text">{{ error }}</p>

    <v-dialog v-model="dialog" max-width="650px" :persistent="true">
      <loading
        :active.sync="isLoadingOrSaving"
        :can-cancel="false"
        loader="dots"
        :color="appColor_Progress"
      />
      <template v-if="!isOptions">
        <v-card>
          <v-toolbar dark flat dense :color="toolbarColor" class="mb-3">
            <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
              <v-icon>close</v-icon>
            </v-btn>
          </v-toolbar>
          <v-card-text>
            <v-row justify="center">
              <h2 class="font-weight-light">
                {{ getExpression('Alerts', 'NoKiosksFound') }}
              </h2>
            </v-row>
          </v-card-text>
        </v-card>
      </template>

      <template v-else>

        <!-- Header Toolbar -->
        <v-toolbar dark flat dense :color="toolbarColor">

          <!-- Cancel -->
          <v-btn fab small @click="cancel" class="elevation-0" :color="toolbarColorDark">
            <v-icon>close</v-icon>
          </v-btn>

          <v-toolbar-title><v-icon class="mx-2">computer</v-icon>{{ title }}</v-toolbar-title>

          <v-spacer />
          
          <!-- OK -->
          <v-toolbar-items>
            <v-btn
              :disabled="selected.length === 0 || isSaving"
              :loading="isSaving"
              dark
              :color="toolbarColorDark"
              @click="select"
              class="elevation-0"
              >
              {{ getExpression('Button', 'OK') }}
            </v-btn>
          </v-toolbar-items>
        </v-toolbar>

        <!-- Data Table -->
        <v-data-table
          v-model="selected"
          :headers="headers"
          :items="filteredOptions"
          :loading="isLoading"
          :search="search"
          :sort-by.sync="sortBy"
          :sort-desc.sync="sortDesc"
          :page.sync="page"
          :footer-props="{'items-per-page-options': [10]}"
          show-select
          :single-select="!multiple"
          item-key="id"
          >
          <v-progress-linear slot="progress" :color="appColor_Progress" indeterminate />

          <template v-slot:top>
            <v-row no-gutters class="mx-3">

              <!-- Kiosk Type -->
              <v-col cols="12" md="4">
                <v-select
                  v-model="kioskTypeId"
                  item-value="id"
                  item-text="description"
                  :items="kioskTypes"
                  label="Type"
                  :disabled="disableKioskType"
                  class="mr-3"
                  >
                  <template v-slot:selection="{ item }">
                    <v-chip small dark :color="getKioskTypeColor(item.id)">
                      <span>{{ item.description }}</span>
                    </v-chip>
                  </template>
                </v-select>
              </v-col>

              <!-- Search -->
              <v-col cols="12" md="8">
                <v-text-field
                  v-model="search"
                  append-icon="search"
                  label="Search"
                  single-line
                  hide-details
                  :disabled="isLoading"
                  clearable
                  class="ml-3"
                  >
                </v-text-field>
              </v-col>
            </v-row>
          </template>
        </v-data-table>
      </template>
    </v-dialog>
  </v-card>
</template>

<script>
import { AppColor, KioskTypeId } from '@/config.js'

export default {
  name: 'KioskSelector',
  emits: ['input'],
  $_veeValidate: {
    name () {
      return this.label
    },
    value () {
      return this.selectedSerialNumbers.length > 0
        ? this.selectedSerialNumbers
        : null
    }
  },
  props: {
    options: {
      type: Array,
      default: null
    },
    value: {
      type: Array,
      default: null
    },
    label: {
      type: String
    },
    multiple: {
      type: Boolean,
      default: false
    },
    disabled: {
      type: Boolean,
      default: false
    },
    chipColor: {
      type: String,
      default: AppColor.Medium
    },
    error: {
      type: String,
      default: null
    },
    toolbarColor: {
      type: String,
      default: AppColor.Medium
    },
    toolbarColorDark: {
      type: String,
      default: AppColor.Dark
    }
  },
  data() {
    return {
      // colors
      appColor_Medium: AppColor.Medium,
      appColor_Progress: AppColor.Progress,
      appColor_Genie: AppColor.KioskTypeGenie,
      appColor_Abby: AppColor.KioskTypeAbby,
      
      // kiosk types
      kioskTypeId_Genie: KioskTypeId.Genie,
      kioskTypeId_Abby: KioskTypeId.Abby,
      
      isLoading: true,
      isSaving: false,

      page: 1,
      selected: [],
      kioskTypeId: KioskTypeId.Genie,
      search: '',
      sortBy: 'name',
      sortDesc: false,

      // options table
      headers: [
        {
          text: 'S/N',
          value: 'serialNumber',
          align: 'left',
          width: '10%'
        },
        {
          text: 'Facility',
          value: 'facility',
          align: 'left',
          width: '30%'
        },
        {
          text: 'Domain',
          value: 'domain',
          align: 'left',
          width: '30%'
        },
        {
          text: 'Organization',
          value: 'organization',
          align: 'left',
          width: '30%'
        }
      ],

      dialog: false,
      selectedKioskIds: []
    }
  },
  computed: {
    title () {
      return this.multiple
        ? 'Select Kiosk(s)'
        : 'Select Kiosk'
    },
    filteredOptions () {
      if (this.isNullOrUndefined(this.options)) return null

      const typeId = this.kioskTypeId
      return this.options.filter(k => k.typeId === typeId)
    },
    kioskNames () {
      return this.options !== null
        ? this.options.map(o => o.serialNumber)
        : []
    },
    selectedIds () {
      return this.value ?? []
    },
    selectedKiosks () {
      const ids = this.selectedIds
      return this.options.filter(o => ids.includes(o.id))
    },
    selectedSerialNumbers () {
      return this.multiple 
        ? this.selectedKiosks.map(k => k.serialNumber)
        : this.selectedKiosks !== null
          ? this.selectedKiosks.length > 0
            ? this.selectedKiosks[0].serialNumber
            : null
          : null
    },
    isLoadingOrSaving () {
      return this.isLoading || this.isSaving
    },
    kioskTypeIds () {
      return this.options
        .map(k => k.typeId)
        .filter(this.onlyUnique)
    },
    isOptions () {
      return this.options.length > 0
    },
    kioskTypes () {
      var kioskTypes = []

      if (this.kioskTypeIds.includes(this.kioskTypeId_Genie)) {
        kioskTypes.push({
          id: KioskTypeId.Genie,
          description: 'Genie'
        })
      }

      if (this.kioskTypeIds.includes(this.kioskTypeId_Abby)) {
        kioskTypes.push({
          id: KioskTypeId.Abby,
          description: 'Abby'
        })
      }

      return kioskTypes
    },
    disableKioskType () {
      return this.kioskTypeIds.length < 2
    },
    selectColor () {
      return this.error
        ? 'red'
        : ''
    },
    selectLabelClass () {
      return this.error
        ? 'red--text'
        : ''
    }
  },
  methods: {
    loadData () {
      this.isLoading = true
      if (this.selectedIds !== null) {
        this.selected = this.options.filter(k => {
          return this.selectedIds.includes(k.id) 
        })
      }
      if (this.kioskTypeIds.includes(this.kioskTypeId_Genie)) {
        this.kioskTypeId = this.kioskTypeId_Genie
      } else {
        this.kioskTypeId = this.kioskTypeId_Abby
      }
      this.isLoading = false
    },

    pickKiosks () {
      const value = this.value
      this.selected = this.options.filter(o => value?.includes(o.id))
      this.loadData()
      this.dialog = true
    },

    select () {
      this.selectedKioskIds = this.selected.map(s => s.id)
      this.dialog = false
      this.emitValue()
    },

    cancel () {
      this.dialog = false
      this.reset()
    },
 
    focus () {
      this.$refs.input.focus()
    },

    reset () {
      const self = this
      setTimeout(() => {
        self.search = ''
        self.isLoading = false
        self.isSaving = false
        self.sortBy = 'name'
        self.sortDesc = false
        self.page = 1
      }, 300)
    },

    getKioskTypeColor(typeId) {
      if (typeId === this.kioskTypeId_Abby) {
        return this.appColor_Abby
      } else if (typeId === this.kioskTypeId_Genie) {
        return this.appColor_Genie
      } else return 'grey'
    },

    removeKiosk (serialNumber) {
      const self = this

      // initialize these arrays when item is removed before any interaction with the datatable
      if (self.selected.length === 0) {
        self.selectedKiosks.forEach(k => self.selected.push(k))
      }

      if (self.selectedKioskIds.length === 0) {
        self.selected.forEach(k => self.selectedKioskIds.push(k.id))
      }

      // standard remove logic
      var id = -1
      const idx1 = self.selected.findIndex(k => k.serialNumber === serialNumber)
      if (idx1 >= 0) {
        id = self.selected[idx1].id
        self.selected.splice(idx1, 1)
      }

      const idx2 = self.selectedKioskIds.findIndex(sid => sid === id)
      if (idx2 >= 0) {
        self.selectedKioskIds.splice(idx2, 1)
      }

      self.emitValue()
    },

    emitValue () {
      // emit the bound valud
      const value = this.selectedKioskIds.length > 0
        ? this.selectedKioskIds
        : null
      this.$emit('input', value)
    },

    handleError (result) {
      this.$error.show({
        title: result.errorSeverity === 0
          ? this.getExpression('FormTitle', 'ValidationFailed')
          : this.getExpression('FormTitle', 'InternalError'),
        text: result.errorMessage,
        severity: result.errorSeverity
      })
    }
  },
  watch: {
    selectedKioskIds () {
      this.emitValue()
    }
  }
}
</script>
